/**
 * WelcomeBackModal - Welcome returning Stripe customers
 *
 * Shows personalized welcome message on first login
 * First modal in two-step welcome flow (before ProfileSetupModal)
 */

import { WELCOME_BACK_MESSAGE, WELCOME_SIGNATURE } from '@/config/brandConfig'
import { useState, useEffect } from 'react'
import { Modal } from '@/components/ui/Modal'
import { getStripeCustomerData } from '@/services/customerService'
import { useAuth } from '@/features/auth'

interface WelcomeBackModalProps {
  isOpen: boolean
  onContinue: () => void
}

export function WelcomeBackModal({ isOpen, onContinue }: WelcomeBackModalProps) {
  const { user } = useAuth()

  const [firstName, setFirstName] = useState('')
  const [isLoading, setIsLoading] = useState(true)

  // Fetch Stripe customer data to get name for greeting (only for existing Stripe customers)
  useEffect(() => {
    async function fetchCustomerData() {
      if (!isOpen || !user?.email) return

      // Check if user has pending invite token (invited user, not existing Stripe customer)
      const pendingInvite = localStorage.getItem('pending_invite_token')

      if (pendingInvite) {
        setFirstName('') // Generic welcome for invited users
        setIsLoading(false)
        return
      }

      try {
        setIsLoading(true)

        const customerData = await getStripeCustomerData(user.email)

        // Get first name for personalized greeting
        setFirstName(customerData.parsedFirstName || '')
      } catch (err) {
        console.error('[WelcomeBackModal] Failed to fetch customer data:', err)
        // Don't show error - just use generic greeting
        setFirstName('')
      } finally {
        setIsLoading(false)
      }
    }

    fetchCustomerData()
  }, [isOpen, user?.email])

  // Greeting text with first name if available
  const greeting = firstName ? `Welcome back, ${firstName}!` : 'Welcome back!'

  // Prevent closing modal (no-op function)
  const preventClose = () => {
    // Modal cannot be closed - user must click Continue
  }

  return (
    <Modal isOpen={isOpen} onClose={preventClose} showCloseButton={false} maxWidth="max-w-lg">
      {isLoading ? (
        // Loading state
        <div className="text-center py-8">
          <div className="text-5xl mb-4">⏳</div>
          <p className="text-gray-600">Loading your information...</p>
        </div>
      ) : (
        // Main content
        <div className="space-y-6">
          {/* Greeting Header */}
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {greeting}
            </h2>
            <p className="text-gray-700 leading-relaxed whitespace-pre-line">
              {WELCOME_BACK_MESSAGE}
            </p>
            <p className="mt-6 text-gray-400" style={{ fontFamily: 'Caveat, cursive', fontSize: '2.5rem', fontWeight: 400 }}>
              {WELCOME_SIGNATURE}
            </p>
          </div>

          {/* Continue Button */}
          <button
            onClick={onContinue}
            className="w-full py-3 bg-gradient-to-r from-[#5c5b44] to-[#6b7850] text-white rounded-lg font-medium shadow-sm hover:from-[#5c5b44] hover:to-[#6b7850] transition-colors"
          >
            Continue
          </button>
        </div>
      )}
    </Modal>
  )
}
